import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  changeCoefficient,
  type EquilibriumSpecies,
  largestShift,
  quotient,
  reactionDirection,
} from "@/lib/tool-kit/chem-reactions/equilibrium";
import {
  kExpression,
  kWithExtent,
  kWithValues,
} from "@/lib/tool-kit/chem-reactions/reaction-input";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

/** English strings for the ICE result block. The calculator spreads these in. */
export const ICE_RESULT_STRINGS = {
  tableHeading: "ICE table",
  columnSpecies: "Species",
  columnInitial: "Initial (M)",
  columnChange: "Change (M)",
  columnEquilibrium: "Equilibrium (M)",
  notInK: "—",
  solidNote: "Pure solids and liquids are in the table but not in K.",
  extent: "Extent of reaction, x",
  kValue: "Equilibrium constant, K",
  direction: "Direction",
  directionForward: "Forward (toward products)",
  directionReverse: "Reverse (toward reactants)",
  directionEquilibrium: "Already at equilibrium",
  workingHeading: "The working",
  expressionLine: "{expression}",
  substitutedLine: "{value} = {k}",
  extentLine: "x = {x}",
  quotientLine: "Q₀ = {q}, which is {comparison} K = {k}",
  quotientLess: "less than",
  quotientMore: "greater than",
  quotientEqual: "equal to",
  valuesLine: "{expression} = {k}",
  shiftOk:
    "The reaction changed the most-affected starting concentration by {percent} %. Below 5 % like this, dropping x from a (C − x) term would also have been safe; this answer does not rely on it.",
  shiftBig:
    "The reaction changed a starting concentration by {percent} %, more than 5 %, so a shortcut that drops x from a (C − x) term would have been wrong. The answer here solves the full equation.",
  concentrationNote:
    "Concentrations are in mol/L. For gases you can enter partial pressures in atm instead, and K is then Kp.",
  sigNote:
    "Rounded to {count} significant figures, the fewest in the values you entered.",
  copy: "Copy results",
  copied: "Copied!",
} as const;

type Strings = typeof ICE_RESULT_STRINGS;

/** "+2x", "−x". */
export function changeSymbol(item: EquilibriumSpecies): string {
  const step = changeCoefficient(item);
  const size = Math.abs(step);
  const sign = step < 0 ? "−" : "+";
  return `${sign}${size === 1 ? "" : size}x`;
}

function row(
  item: EquilibriumSpecies,
  initial: number,
  equilibrium: number,
  x: number,
  sig: number,
  strings: Strings,
  fmt: NumberFormatter
): string[] {
  if (item.solid) {
    return [item.label, strings.notInK, strings.notInK, strings.notInK];
  }
  const change = changeCoefficient(item) * x;
  const shown = formatSig(fmt, Math.abs(change), sig);
  const signed = `${change < 0 ? "−" : "+"}${shown}`;
  return [
    item.label,
    formatSig(fmt, initial, sig),
    `${changeSymbol(item)} = ${signed}`,
    formatSig(fmt, equilibrium, sig),
  ];
}

export function IceResults({
  species,
  initial,
  equilibrium,
  x,
  k,
  givenK,
  startKnown = true,
  sig,
  strings,
  fmt,
}: {
  species: readonly EquilibriumSpecies[];
  initial: readonly number[];
  equilibrium: readonly number[];
  x: number;
  k: number;
  /** K was typed in (solve mode), so the working derives x from it. */
  givenK: boolean;
  /** False when only equilibrium concentrations were entered: no initial or change columns. */
  startKnown?: boolean;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const kText = formatSig(fmt, k, sig);
  const q0 = quotient(species, initial);
  const direction = reactionDirection(q0, k);
  const directionText = {
    forward: strings.directionForward,
    reverse: strings.directionReverse,
    equilibrium: strings.directionEquilibrium,
  }[direction];
  const shift = givenK ? largestShift(species, initial, x) : null;
  const percent = shift === null ? "" : formatSig(fmt, shift * 100, 2);
  const startText = initial.map((value) => formatSig(fmt, value, sig));
  const zero = initial.map((value) => value === 0);
  const live = species.filter((item) => !item.solid);
  const lines = startKnown
    ? [
        kExpression(species),
        t(strings.substitutedLine, {
          value: kWithExtent(species, startText, zero),
          k: kText,
        }),
        t(strings.extentLine, { x: formatSig(fmt, x, sig + 1) }),
      ]
    : [kExpression(species)];
  if (startKnown && Number.isFinite(q0)) {
    let comparison: string = strings.quotientEqual;
    if (direction === "forward") {
      comparison = strings.quotientLess;
    } else if (direction === "reverse") {
      comparison = strings.quotientMore;
    }
    lines.push(
      t(strings.quotientLine, {
        q: formatSig(fmt, q0, sig),
        comparison,
        k: kText,
      })
    );
  }
  const check = kWithValues(
    species,
    equilibrium.map((value) => formatSig(fmt, value, sig + 1))
  );
  lines.push(t(strings.valuesLine, { expression: check, k: kText }));
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.kValue} value={kText} />
        {startKnown ? (
          <>
            <StatCard label={strings.extent} value={formatSig(fmt, x, sig)} />
            <StatCard label={strings.direction} value={directionText} />
          </>
        ) : null}
      </StatGrid>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        {startKnown ? (
          <DataTable
            head={[
              strings.columnSpecies,
              strings.columnInitial,
              strings.columnChange,
              strings.columnEquilibrium,
            ]}
            rows={species.map((item, index) =>
              row(
                item,
                initial[index],
                equilibrium[index],
                x,
                sig,
                strings,
                fmt
              )
            )}
          />
        ) : (
          <DataTable
            head={[strings.columnSpecies, strings.columnEquilibrium]}
            rows={species.map((item, index) => [
              item.label,
              item.solid
                ? strings.notInK
                : formatSig(fmt, equilibrium[index], sig),
            ])}
          />
        )}
        {live.length < species.length ? (
          <p className="text-muted-foreground text-xs">{strings.solidNote}</p>
        ) : null}
      </div>
      {shift === null ? null : (
        <Notice>
          {t(shift < 0.05 ? strings.shiftOk : strings.shiftBig, { percent })}
        </Notice>
      )}
      <Notice>{t(strings.sigNote, { count: fmt(sig) })}</Notice>
      <p className="text-muted-foreground text-xs">
        {strings.concentrationNote}
      </p>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <div className={WORKING_BOX}>
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={`${kExpression(species)} = ${kText}`}
      />
    </>
  );
}
